export type AttendanceGroupMember = {
  studioClassId: string
  name: string
  dayOfWeek: number
  startTime: string | null
}

export type AttendanceGroup = {
  id: string
  name: string
  requiredPerWeek: number
  alternatives: AttendanceGroupMember[]
  isMeaningful: boolean
}

export type SaveAttendanceGroup = {
  name: string
  requiredPerWeek: number
  studioClassIds: string[]
}

export async function fetchAttendanceGroups(): Promise<AttendanceGroup[]> {
  const res = await fetch('/api/attendance-groups')
  if (!res.ok) throw new Error(`Could not load the groups (${res.status})`)
  return res.json()
}

async function send(
  method: 'POST' | 'PUT' | 'DELETE',
  path: string,
  body?: SaveAttendanceGroup,
): Promise<AttendanceGroup[]> {
  const res = await fetch(`/api/attendance-groups${path}`, {
    method,
    headers: body ? { 'Content-Type': 'application/json' } : {},
    body: body ? JSON.stringify(body) : undefined,
  })

  if (!res.ok) {
    const problem = await res.json().catch(() => null)
    throw new Error(problem?.error ?? `That could not be saved (${res.status})`)
  }

  return res.json()
}

export const createGroup = (input: SaveAttendanceGroup) => send('POST', '/', input)
export const updateGroup = (id: string, input: SaveAttendanceGroup) => send('PUT', `/${id}`, input)
export const deleteGroup = (id: string) => send('DELETE', `/${id}`)

/**
 * The level a class name appears to be for.
 *
 * **A filter for the picker, never the rule itself (D-170).** The groups are
 * organised by level, so narrowing a list of three hundred classes to the eight
 * that could plausibly belong together is most of the work. But the membership
 * is still whatever Tonya ticks — so if this reads a level wrong it costs her a
 * scroll, where inferring the *group* from a name would cost a dancer her place
 * on a team.
 *
 * Handles the shapes the real export actually uses:
 * - `COMP_TECH_BALLET_2 | MO 415 | MAC` — a plain level
 * - `COMP_TECH_COMBO_3.5/4 | MO 630` — spanning two, and belonging to both
 * - `COMP_TECH_BALLET_1PRETEAM | MO 400` — a level with a suffix
 *
 * Everything after the first pipe is the studio's own annotation — day, time,
 * ages, teacher — and reading a level out of `TU 430 | 9/10` would offer levels
 * 9 and 10 that do not exist.
 */
export function levelsIn(className: string): string[] {
  const head = className.split('|')[0]
  const found = new Set<string>()

  // Levels follow the discipline, after an underscore: BALLET_3.5, COMBO_4/5/6.
  for (const match of head.matchAll(/_(\d+(?:\.\d+)?(?:\/\d+(?:\.\d+)?)*)/g)) {
    for (const level of match[1].split('/')) found.add(level)
  }

  return [...found]
}

/** Every level the studio runs, for the filter chips. Sorted numerically. */
export function allLevels(classNames: string[]): string[] {
  const levels = new Set<string>()
  for (const name of classNames) for (const level of levelsIn(name)) levels.add(level)

  return [...levels].sort((a, b) => Number(a) - Number(b))
}

/**
 * Narrows the class list to what Tonya is looking for.
 *
 * Level and text together, because a level alone still leaves every discipline
 * in it and she is usually after "the level 2 tech classes".
 */
export function matchesFilter(className: string, level: string | null, search: string): boolean {
  if (level !== null && !levelsIn(className).includes(level)) return false

  const term = search.trim().toLowerCase()
  return term === '' || className.toLowerCase().includes(term)
}
